import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TeamsPerson } from '@atlas/shared';

import { api } from '../../services/api';

/**
 * Debounce window for the people search.
 *
 * A request per keystroke is not one HTTP call — the server resolves people out
 * of the user's chat memberships, which is several Graph round trips, against a
 * throttle budget shared with the message polling. 250ms is below the threshold
 * where the list feels laggy and still collapses a typed name into one or two
 * requests instead of eight.
 */
export const TEAMS_PEOPLE_DEBOUNCE_MS = 250;

/** As many as the panel can show without becoming a scroll-hunt at 280px. */
export const TEAMS_PEOPLE_LIMIT = 15;

/**
 * The mention popup asks for fewer: it floats over the composer inside a narrow
 * panel, and a tall list there covers the message being written.
 */
export const TEAMS_MENTION_LIMIT = 6;

/**
 * Trailing-edge debounce of a string.
 *
 * Exported for the tests, which need to assert the *debouncing* rather than
 * wait on it — driving it directly is far more honest than sleeping.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (value === debounced) return;
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
    // `debounced` is deliberately excluded from the deps: including it would
    // restart the timer each time the debounced value settles, which is a
    // wasted render at best and a stuck value at worst. It is only read to
    // skip a no-op, and it is always current for the `value` that triggered
    // this run.
  }, [value, delayMs]);

  return debounced;
}

export interface UseTeamsPeopleOptions {
  /** Raw, undebounced query straight off the input. */
  query: string;
  /** False while the surface is closed — a closed picker must cost nothing. */
  enabled?: boolean;
  limit?: number;
  /**
   * Minimum characters before anything is fetched. The picker opens with an
   * empty field and shows recent people (0); the mention popup requires the
   * user to have committed to a name (1).
   */
  minLength?: number;
  /**
   * Narrows the result to one conversation's own members.
   *
   * The @mention popup passes it; the new-chat picker must not, because there
   * is no conversation yet. Omitting it yields everyone the user shares any
   * chat with, which is the wider list this hook started life returning.
   */
  chatId?: string;
}

export interface UseTeamsPeopleResult {
  people: TeamsPerson[];
  isLoading: boolean;
  errorMessage: string | undefined;
  /** True while the field has moved on but the request has not caught up. */
  isDebouncing: boolean;
}

/**
 * Debounced people lookup, shared by the new-chat picker and the @mention
 * popup so the two cannot drift into different query keys and re-fetch each
 * other's results.
 *
 * These are people you already have conversations with — see `api.teams.listPeople`.
 */
export function useTeamsPeople({
  query,
  enabled = true,
  limit = TEAMS_PEOPLE_LIMIT,
  minLength = 0,
  chatId,
}: UseTeamsPeopleOptions): UseTeamsPeopleResult {
  const trimmed = query.trim();
  const debouncedQuery = useDebouncedValue(trimmed, TEAMS_PEOPLE_DEBOUNCE_MS);

  const isLongEnough = debouncedQuery.length >= minLength;
  const isEnabled = enabled && isLongEnough;

  const { data, isFetching, error } = useQuery({
    // `chatId` is part of the key, not just the request: the scoped and
    // unscoped lists are different answers, and sharing a cache entry would let
    // the new-chat picker serve its wider list to a mention popup.
    queryKey: ['teams', 'people', debouncedQuery, limit, chatId ?? null],
    queryFn: () => api.teams.listPeople({ q: debouncedQuery || undefined, limit, chatId }),
    enabled: isEnabled,
    // The set of people you chat with does not turn over minute to minute, and
    // backspacing through a query must not re-fetch every prefix it passes.
    staleTime: 60_000,
    retry: 1,
  });

  return {
    people: data ?? [],
    isLoading: isEnabled && isFetching,
    errorMessage: error instanceof Error ? error.message : undefined,
    isDebouncing: trimmed !== debouncedQuery,
  };
}
